
import { defineComponent, PropType, computed, reactive, onBeforeMount } from 'vue'

// components
import Modal from '@/common/components/UI/Modal/index.vue'
import TheSelect from '@/common/components/UI/TheSelect/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'
import TheInput from '@/common/components/UI/TheInput/index.vue'
import RadioGroup from '@/common/components/UI/RadioGroup/index.vue'
import Loader from '@/common/components/UI/Loader/index.vue'

// types
import {
    CalculateCostStandardsPayload,
    CostStandards,
    NewCostStandard,
    NewCostStandardsPayload,
} from '@/new-services/types/tms'
import { CityT } from '@/core/types/cities.types'

// realisations
import { cities } from '@/core/realisations/cities.realisation'

// services
import tmsAPI from '@/new-services/tms.service'

// composable
import useNotifications from '@/common/composable/useNotifications'

// validations
import useVuelidate from '@vuelidate/core'
import { required } from '@vuelidate/validators'

const TRANSPORTATION_TYPES = [
    { id: 1, name: 'LTL' },
    { id: 2, name: 'FTL' },
]

const QUARTERS = [
    { id: 1, name: 'I квартал' },
    { id: 2, name: 'II квартал' },
    { id: 3, name: 'III квартал' },
    { id: 4, name: 'IV квартал' },
]

const MONTHS = [
    'Январь',
    'Февраль',
    'Март',
    'Апрель',
    'Май',
    'Июнь',
    'Июль',
    'Август',
    'Сентябрь',
    'Октябрь',
    'Ноябрь',
    'Декабрь',
]

export default defineComponent({
    components: {
        Modal,
        TheSelect,
        TheButton,
        TheInput,
        RadioGroup,
        Loader,
    },
    props: {
        isCreate: {
            type: Boolean,
            default: false,
        },
        isUpdate: {
            type: Boolean,
            default: false,
        },
        initialValue: {
            type: Object as PropType<CostStandards>,
            default: null,
        },
    },
    emits: ['close', 'created', 'updated'],
    setup(props, { emit }) {
        const notifications = useNotifications()
        const year = new Date().getFullYear()

        const state = reactive({
            is_loading: false,
            is_calculate_loading: false,
            quarter: 1,
            //
            sender_city: null as number | null,
            receiver_city: null as number | null,
            transit_cities: [] as Array<number | null>,
            invoiceType: 1,
            //
            costStandards: [] as NewCostStandard[],
        })

        const state_form$ = useVuelidate(
            {
                sender_city: { required },
                receiver_city: { required },
            },
            state
        )

        const cities_state = reactive({
            is_loading: false,
            is_failed: false,
            list: [] as CityT[],
        })

        const loadCities = async () => {
            try {
                cities_state.is_loading = true

                const response = await cities.getAllList(1, undefined, 9999)
                cities_state.list = response.data

                cities_state.is_failed = false
            } catch (error) {
                console.error(error)
                cities_state.is_failed = true
                notifications.notifyError({
                    error,
                    title: 'При получении списка городов произошла ошибка',
                })
            } finally {
                cities_state.is_loading = false
            }
        }

        const addTransitCity = () => {
            state.transit_cities.push(null)
        }

        const deleteTransitCity = (transit_city_index: number) => {
            const answer = confirm(
                `Вы действительно хотите удалить транзитный город №${transit_city_index + 1}?`
            )
            if (!answer) return

            state.transit_cities.splice(transit_city_index, 1)
        }

        const onCancel = () => {
            if (state.is_loading) return
            emit('close')
        }

        const createCostStandards = async () => {
            const cityIds = [...state.transit_cities]
            cityIds.unshift(state.sender_city)
            cityIds.push(state.receiver_city)

            try {
                state.is_loading = true

                const payload: NewCostStandardsPayload = {
                    cityIds: cityIds.filter((cityId) => cityId !== null) as number[],
                    invoiceType: state.invoiceType,
                    year,
                    costStandards: [...state.costStandards],
                }

                await tmsAPI.createCostStandards(payload)

                emit('created')

                notifications.notify({
                    type: 'success',
                    title: 'Нормы себестоимости созданы',
                })
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При создании норм себестоимости произошла ошибка',
                })
            } finally {
                state.is_loading = false
            }
        }

        const updateCostStandards = async () => {
            const cityIds = [...state.transit_cities]
            cityIds.unshift(state.sender_city)
            cityIds.push(state.receiver_city)

            try {
                state.is_loading = true

                const costStandards = state.costStandards.map((item) => {
                    return {
                        ...item,
                        costPerWeight: Number(item.costPerWeight),
                        costStandardsByMonth: item.costStandardsByMonth.map((subitem) => {
                            return {
                                capacityId: Number(subitem.capacityId),
                                cost: Number(subitem.cost),
                                costPerCubature: Number(subitem.costPerCubature),
                            }
                        }),
                    }
                })

                const payload: NewCostStandardsPayload = {
                    cityIds: cityIds.filter((cityId) => cityId !== null) as number[],
                    invoiceType: state.invoiceType,
                    year,
                    costStandards,
                }

                await tmsAPI.updateNewCostStandards(props.initialValue.id, payload)

                emit('updated')

                notifications.notify({
                    type: 'success',
                    title: 'Нормы себестоимости сохранены',
                })
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При сохранении норм себестоимости произошла ошибка',
                })
            } finally {
                state.is_loading = false
            }
        }

        const onConfirm = () => {
            if (state.is_loading) return

            state_form$.value.$touch()
            if (state_form$.value.$invalid) return

            if (props.isCreate) {
                createCostStandards()
                return
            }

            if (props.isUpdate) {
                updateCostStandards()
                return
            }
        }

        const setCostStandards = () => {
            for (let i = 1; i <= 12; i++) {
                const costStandardsByMonth = []
                for (let k = 6; k > 0; k--) {
                    if (k !== 4) {
                        costStandardsByMonth.push({
                            capacityId: k,
                            cost: null,
                            costPerCubature: null,
                        })
                    }
                }
                state.costStandards.push({
                    monthId: i,
                    costPerWeight: null,
                    costStandardsByMonth,
                })
            }
        }

        const setInfo = async () => {
            if (!props.initialValue) return

            try {
                state.is_loading = true

                state.invoiceType = props.initialValue.invoiceTypeId

                const cityIds = [...props.initialValue.cityIds]
                state.sender_city = cityIds[0]
                state.receiver_city = cityIds[cityIds.length - 1]
                cityIds.splice(0, 1)
                cityIds.splice(cityIds.length - 1, 1)
                if (cityIds.length) {
                    state.transit_cities = cityIds
                }

                const response = await tmsAPI.getCostStandard(props.initialValue.id)

                if (response.data.data) {
                    response.data.data.costStandards.forEach((capacity) => {
                        const itemFound = state.costStandards.find(
                            (item) => item.monthId === capacity.monthId
                        )
                        if (itemFound) {
                            itemFound.costPerWeight = capacity.costPerWeight
                            capacity.capacities.forEach((subCapacity) => {
                                const subitemFound = itemFound.costStandardsByMonth.find(
                                    (subCost) => subCost.capacityId === subCapacity.capacityId
                                )
                                if (subitemFound) {
                                    subitemFound.cost = subCapacity.cost
                                    subitemFound.costPerCubature = subCapacity.costPerCubature
                                }
                            })
                        }
                    })
                }
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'Произошла ошибка',
                })
            } finally {
                state.is_loading = false
            }
        }

        onBeforeMount(async () => {
            setCostStandards()
            loadCities()
            setInfo()
        })

        const modalTitle = computed(() => {
            if (props.isCreate) return 'Создать'
            if (props.isUpdate) return 'Редактировать'
            return null
        })

        const confirmButtonText = computed(() => {
            if (props.isCreate) return 'Создать'
            if (props.isUpdate) return 'Сохранить'
            return 'Подтвердить'
        })

        const getItems = computed(() => {
            return state.costStandards.filter(
                (item) =>
                    (state.quarter - 1) * 3 < item.monthId && item.monthId <= state.quarter * 3
            )
        })

        const calculate = async () => {
            try {
                state.is_calculate_loading = true

                const cityIds = [...state.transit_cities]
                cityIds.unshift(state.sender_city)
                cityIds.push(state.receiver_city)

                const costPerWeightByMonth = state.costStandards.map((item) => {
                    return {
                        monthId: item.monthId as number,
                        costPerWeight: item.costPerWeight as number,
                    }
                })

                const payload: CalculateCostStandardsPayload = {
                    cityIds: cityIds.filter((cityId) => cityId !== null) as number[],
                    costPerWeightByMonth,
                }

                const response = await tmsAPI.calculateCostStandards(payload)
                response.data.data.forEach((item) => {
                    const itemFound = state.costStandards.find(
                        (costStandard) => costStandard.monthId === item.monthId
                    )

                    item.costStandardCapacities.forEach((subitem) => {
                        if (itemFound) {
                            const subitemFound = itemFound.costStandardsByMonth.find(
                                (subCostStandard) => subCostStandard.capacityId === subitem.id
                            )

                            if (subitemFound) {
                                subitemFound.cost = subitem.cost
                                subitemFound.costPerCubature = subitem.costPerCubature
                            }
                        }
                    })
                })
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'Произошла ошибка',
                })
            } finally {
                state.is_calculate_loading = false
            }
        }

        const isLtl = computed(() => state.invoiceType === 1)
        const isLoading = computed(
            () => state.is_loading || state.is_calculate_loading || cities_state.is_loading
        )

        return {
            state,
            state_form$,
            cities_state,
            loadCities,
            addTransitCity,
            deleteTransitCity,
            onCancel,
            modalTitle,
            confirmButtonText,
            onConfirm,
            TRANSPORTATION_TYPES,
            QUARTERS,
            getItems,
            MONTHS,
            calculate,
            isLtl,
            isLoading,
        }
    },
})
